/**
 * The vendor's own payout history — step 14. Read-only, same minimal
 * unfiltered-query shape as OwnerStatementsPage.tsx: no `vendorId` filter
 * anywhere here, because `vendor_disbursements_vendor_select` already
 * scopes the query to this vendor's own rows — and, per that policy, only
 * once `status IN ('PAID', 'SETTLED_EXTERNALLY')`, so nothing merely
 * APPROVED or TRANSFERRED ever appears here.
 *
 * No PDF download, unlike the owner statement — decision #3 confirmed no
 * frozen document for vendor payouts; the disbursement + its line items
 * (which work orders were paid, for how much) is the record, expandable
 * inline instead.
 */

import { useState } from 'react';
import { useList } from '@refinedev/core';
import type { VendorDisbursementDto } from 'shared';
import { Bento, BentoCell } from '../../../components/Bento.js';
import { DataTable, type Column } from '../../../components/DataTable.js';

function payoutStatusLabel(row: VendorDisbursementDto): {
  text: string;
  tone?: 'positive' | 'negative';
} {
  if (row.status === 'PAID')
    return { text: `Paid ${row.paidAt?.slice(0, 10) ?? ''}`, tone: 'positive' };
  if (row.status === 'SETTLED_EXTERNALLY') return { text: 'Settled by check/wire' };
  return { text: 'Pending' };
}

export function VendorDisbursementsPage() {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data, isLoading, error } = useList<VendorDisbursementDto>({
    resource: 'vendor-disbursements',
    pagination: { mode: 'off' },
  });

  const columns: Column<VendorDisbursementDto>[] = [
    { key: 'amount', header: 'Amount', numeric: true, render: (row) => `$${row.amount}` },
    {
      key: 'status',
      header: 'Status',
      render: (row) => {
        const { text, tone } = payoutStatusLabel(row);
        return (
          <span
            className={`pill${tone === 'positive' ? ' pill--positive' : tone === 'negative' ? ' pill--negative' : ''}`}
          >
            {text}
          </span>
        );
      },
    },
    {
      key: 'lines',
      header: 'Work orders',
      render: (row) => (
        <button
          type="button"
          className="button button--quiet"
          onClick={() => setExpandedId((id) => (id === row.id ? null : row.id))}
        >
          {expandedId === row.id ? 'Hide' : `${String(row.lines.length)} work order(s)`}
        </button>
      ),
    },
  ];

  const expandedRow = (data?.data ?? []).find((row) => row.id === expandedId);

  return (
    <section>
      <h1>Your payouts</h1>
      <p className="muted">
        Every completed work order paid out to you, grouped by payout. Only payouts that have
        actually reached you appear here.
      </p>
      <Bento>
        <BentoCell span="full">
          <DataTable
            columns={columns}
            rows={data?.data ?? []}
            rowKey={(row) => row.id}
            isLoading={isLoading}
            error={error}
            emptyMessage="No payouts yet."
          />
        </BentoCell>
        {expandedRow ? (
          <BentoCell span="full" title="Work orders in this payout">
            <table className="table">
              <thead>
                <tr>
                  <th>Work order</th>
                  <th>Property</th>
                  <th className="num">Amount</th>
                </tr>
              </thead>
              <tbody>
                {expandedRow.lines.map((line) => (
                  <tr key={line.id}>
                    <td>{line.workOrderTitle}</td>
                    <td>
                      {line.propertyName}, {line.unitLabel}
                    </td>
                    <td className="num">${line.amount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </BentoCell>
        ) : null}
      </Bento>
    </section>
  );
}
